/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faCheck, faExclamation } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert, Badge, Button, Col, FormGroup, Input, Label, Progress, Row, Table } from "reactstrap";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { CONFIG_TYPES } from "../../../../actions/Tenants/config/constants";
import { FRUIT_LIMIT_KEYS, getRipewiseConfig, RipewiseConfig } from "../../../../actions/Tenants/config/ripewise";
import { useDevicesQuery } from "../../../../hooks/useDeviceMutations";
import { useTenantFacilityLocations } from "../../../../hooks/useFacilityLocationQueries";
import { useGatewayRipeningHealth, useRipeningPrograms } from "../../../../hooks/useRipeningPrograms";
import { useSaveTenantConfig, useTenantActiveConfigs } from "../../../../hooks/useTenantConfigQueries";
import { FormContainer } from "../../../Helper/FormContainer";
import FullPageLayout from "../../Layout/FullPageLayout";
import RoomLinksModal from "../RoomLinksModal";
import TenantPageWrappedInConfigProvider from "../TenantPageWrappedInConfigProvider";

const RIPENING_ROOM_GATEWAY = "ripening_room_gateway";

type StepStatus = "done" | "problem" | "todo";

const STATUS_LABEL: Record<StepStatus, string> = {
    done: "Done",
    problem: "Needs attention",
    todo: "To do",
};

// * Vertical stepper: a circle per step on a line, the current step highlighted.
const stepperCss = css`
    list-style: none;
    padding: 0;
    margin: 0;

    li { position: relative; }
    /* the line between two circles */
    li:not(:last-child)::after {
        content: "";
        position: absolute;
        left: calc(0.75rem + 17px);
        top: calc(0.5rem + 36px);
        bottom: calc(-0.5rem + 2px);
        width: 2px;
        background: var(--bs-border-color);
    }
    li.done:not(:last-child)::after { background: var(--bs-success); }

    button {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        width: 100%;
        padding: 0.5rem 0.75rem;
        border: 0;
        border-left: 4px solid transparent;
        border-radius: 0.375rem;
        background: transparent;
        text-align: left;
        color: var(--bs-body-color);
    }
    button:hover { background: var(--bs-tertiary-bg); }
    li.current button {
        background: var(--bs-primary-bg-subtle);
        border-left-color: var(--bs-primary);
    }

    .circle {
        flex: 0 0 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        position: relative;
        z-index: 1;
        border: 2px solid var(--bs-border-color);
        background: var(--bs-body-bg);
        color: var(--bs-secondary-color);
    }
    li.done .circle { background: var(--bs-success); border-color: var(--bs-success); color: #fff; }
    li.problem .circle { background: var(--bs-warning); border-color: var(--bs-warning); color: #000; }
    li.current .circle { border-color: var(--bs-primary); box-shadow: 0 0 0 3px var(--bs-primary-bg-subtle); }
    li.current.todo .circle { color: var(--bs-primary); }

    .title { display: block; line-height: 1.2; }
    li.current .title { font-weight: 600; }
    .status { display: block; font-size: 0.8rem; color: var(--bs-secondary-color); }
    li.problem .status { color: var(--bs-warning-text-emphasis); }
`;

function WrappedRipewiseSetupWizard() {
    return <TenantPageWrappedInConfigProvider>
        <RipewiseSetupWizard />
    </TenantPageWrappedInConfigProvider>;
}

export default WrappedRipewiseSetupWizard;

function RipewiseSetupWizard() {
    const { tenant_id: tenantId = "" } = useParams();
    const config = useConfig();
    const configsQuery = useTenantActiveConfigs(tenantId);
    const storedRow = (configsQuery.data ?? []).find((i) => i.type === CONFIG_TYPES.RIPEWISE && i.value === CONFIG_TYPES.RIPEWISE);
    const stored = (storedRow?.config ?? {}) as Partial<RipewiseConfig>;
    const ripewise = getRipewiseConfig(configsQuery.data);
    const saveMutation = useSaveTenantConfig(tenantId);
    // * Each save writes the whole config over the stored one, so it waits until that is loaded:
    // * saving over a missing one would wipe the tenant ID, the limits and control.
    const cannotSave = saveMutation.isPending || !configsQuery.isSuccess;
    let error: string | null = null;
    if (configsQuery.isError) error = "The RipeWise config could not be loaded, so nothing can be saved. Reload the page.";
    else if (saveMutation.isError) error = "Saving the RipeWise config failed. Try again.";
    const save = (changes: Partial<RipewiseConfig>) => {
        if (!configsQuery.isSuccess) return;
        saveMutation.mutate({
            type: CONFIG_TYPES.RIPEWISE,
            value: CONFIG_TYPES.RIPEWISE,
            note: "RipeWise setup",
            position: 0,
            config: { ...stored, ...changes },
        });
    };
    const scope = { tenantId };

    const { data: devices } = useDevicesQuery();
    const gateways = useMemo(
        () => (devices ?? []).filter((device) => device.type === RIPENING_ROOM_GATEWAY && device.tenant_id === tenantId),
        [devices, tenantId],
    );
    const [gatewayId, setGatewayId] = useState("");
    useEffect(() => {
        if (!gatewayId && gateways.length) setGatewayId(gateways[0].UID);
    }, [gateways, gatewayId]);
    const health = useGatewayRipeningHealth(ripewise.enabled ? gatewayId || undefined : undefined, scope);

    const { data: cellsData, refetch: refetchCells } = useTenantFacilityLocations(tenantId, { offset: 0, limit: 1000, type: "ripening_room" });
    const cells = cellsData?.results ?? [];
    const linkedCells = cells.filter((cell) => !!cell.gateway && !!cell.controller_room);
    const [roomLinksOpen, setRoomLinksOpen] = useState(false);

    const fruitTypes: Array<{ value: string; text?: string }> = config.fruit_types ?? [];
    const programsQuery = useRipeningPrograms(gatewayId || undefined, scope, undefined, ripewise.enabled);
    const programs = programsQuery.data?.results ?? [];
    const fruitsWithoutProgram = fruitTypes.filter((fruit) => !programs.some((program) => program.fruit_type === fruit.value));

    // * Step 1 form
    const [serviceTenantId, setServiceTenantId] = useState("");
    // * Step 4 form: fruit -> limit key -> input text
    const [limits, setLimits] = useState<Record<string, Record<string, string>>>({});
    const storedLimits = (): Record<string, Record<string, string>> => Object.fromEntries((ripewise.fruit_limits ?? []).filter((row) => row?.fruit_type).map((row) => [
        row.fruit_type as string,
        Object.fromEntries(FRUIT_LIMIT_KEYS.map((key) => [key, row[key] === null || row[key] === undefined ? "" : String(row[key])])),
    ]));
    useEffect(() => {
        setServiceTenantId(ripewise.tenant_id ?? "");
        setLimits(storedLimits());
    }, [storedRow?.id]);

    const healthStatus = (): StepStatus => {
        if (!gatewayId) return "todo";
        if (health.data?.ok) return "done";
        return health.isError || health.data ? "problem" : "todo";
    };
    const limitRows = Object.entries(limits).filter(([, row]) => Object.values(row).some((value) => value !== ""));

    const steps: Array<{ title: string; status: StepStatus; body: JSX.Element }> = [
        {
            title: "Enable RipeWise",
            status: ripewise.enabled ? "done" : "todo",
            body: <>
                <p className="text-muted">RipeWise controls the ripening rooms and reads their sensors. While it is disabled, EYE does not talk to RipeWise for this tenant.</p>
                <Alert color={ripewise.enabled ? "success" : "secondary"} className="d-flex justify-content-between align-items-center">
                    <span>RipeWise is <b>{ripewise.enabled ? "enabled" : "disabled"}</b> for this tenant.</span>
                    {ripewise.enabled
                        ? <Button color="danger" outline disabled={cannotSave} onClick={() => save({ enabled: false })}>Disable RipeWise</Button>
                        : <Button color="primary" disabled={cannotSave} onClick={() => save({ enabled: true })}>Enable RipeWise</Button>}
                </Alert>
                <h5 className="mt-4">RipeWise tenant ID</h5>
                <p className="text-muted">The ID RipeWise knows this tenant by: the one the gateway sends its data under. Leave it empty when that is this tenant&apos;s own ID ({tenantId}).</p>
                <Alert color="info" className="small">Not sure which ID the gateway uses? Ask a developer to check the tenant ID of the gateway in the RipeWise control panel before you save it.</Alert>
                <FormGroup>
                    <Label for="ripewise-tenant-id">RipeWise tenant ID</Label>
                    <Input id="ripewise-tenant-id" value={serviceTenantId} placeholder={tenantId} onChange={(event) => setServiceTenantId(event.target.value)} />
                </FormGroup>
                <Button color="primary" disabled={cannotSave || serviceTenantId.trim() === (ripewise.tenant_id ?? "")} onClick={() => save({ tenant_id: serviceTenantId.trim() })}>Save tenant ID</Button>
            </>,
        },
        {
            title: "Gateway",
            status: gateways.length ? healthStatus() : "todo",
            body: <>
                <p className="text-muted">The gateway in the ripening facility talks to the room controllers. Add it in Device Management as a device of type <b>ripening room gateway</b> for this tenant; it then shows up here and EYE checks that it reaches RipeWise.</p>
                {!gateways.length && <Alert color="info">This tenant has no gateway yet. <Link to="/device-management">Add it in Device Management</Link>.</Alert>}
                {!!gateways.length && <>
                    <FormGroup>
                        <Label for="ripewise-gateway">Gateway</Label>
                        <Input id="ripewise-gateway" type="select" value={gatewayId} onChange={(event) => setGatewayId(event.target.value)}>
                            {gateways.map((gateway) => <option key={gateway.UID} value={gateway.UID}>{gateway.name || gateway.UID}</option>)}
                        </Input>
                    </FormGroup>
                    {!ripewise.enabled && <Alert color="secondary">Enable RipeWise first (step 1) to check the connection.</Alert>}
                    {ripewise.enabled && health.isLoading && <p>Checking the connection…</p>}
                    {ripewise.enabled && health.isError && <Alert color="warning">
                        RipeWise does not know this gateway for this tenant. Check that the gateway is registered in RipeWise and that the RipeWise tenant ID (step 1) is the one the gateway sends its data under.
                    </Alert>}
                    {health.data && <Table size="sm" className="w-auto">
                        <tbody>
                            {Object.entries(health.data.hops).map(([hop, value]) => (
                                <tr key={hop}>
                                    <td className="text-capitalize">{hop}</td>
                                    <td><Badge color={value.ok ? "success" : "danger"}>{value.ok ? "OK" : "Not reachable"}</Badge></td>
                                    <td className="text-muted small">{value.detail}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>}
                    {gatewayId && <Link to={`/device-management/${gatewayId}/view/gateway-status`}>Open the gateway status</Link>}
                </>}
            </>,
        },
        {
            title: "Link the rooms",
            status: linkedCells.length ? "done" : "todo",
            body: <>
                <p className="text-muted">Each ripening room in EYE belongs to a room on the gateway&apos;s controller. Link them so EYE can show the room status and send programs to the right room.</p>
                <p><b>{linkedCells.length}</b> of <b>{cells.length}</b> rooms linked.</p>
                {!cells.length && <Alert color="info">This tenant has no ripening rooms yet. <Link to={`/tenants/${tenantId}/facility-locations`}>Add them under facility locations</Link>.</Alert>}
                <Button color="primary" disabled={!cells.length || !gateways.length} onClick={() => setRoomLinksOpen(true)}>Link rooms</Button>
            </>,
        },
        {
            title: "Temperature limits per fruit",
            status: limitRows.length ? "done" : "todo",
            body: <>
                <p className="text-muted">The air and product temperature limits of each fruit. Every program of that fruit gets them, and they cannot be changed in the program. Leave a value empty to let the program set it.</p>
                {!fruitTypes.length && <Alert color="info">This tenant has no fruit types yet.</Alert>}
                {!!fruitTypes.length && <Table size="sm" className="w-auto">
                    <thead>
                        <tr><th>Fruit</th><th>Air min °C</th><th>Air max °C</th><th>Product min °C</th><th>Product max °C</th></tr>
                    </thead>
                    <tbody>
                        {fruitTypes.map((fruit) => (
                            <tr key={fruit.value}>
                                <td>{fruit.text || fruit.value}</td>
                                {FRUIT_LIMIT_KEYS.map((key) => (
                                    <td key={key}>
                                        <Input
                                            bsSize="sm"
                                            type="number"
                                            step="any"
                                            aria-label={`${fruit.value} ${key}`}
                                            value={limits[fruit.value]?.[key] ?? ""}
                                            onChange={(event) => setLimits((current) => ({ ...current, [fruit.value]: { ...current[fruit.value], [key]: event.target.value } }))}
                                            style={{ width: "6rem" }}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>}
                <Button color="primary" disabled={cannotSave} onClick={() => save({
                    fruit_limits: limitRows.map(([fruit, row]) => ({
                        fruit_type: fruit,
                        ...Object.fromEntries(FRUIT_LIMIT_KEYS.filter((key) => row[key] !== "" && row[key] !== undefined).map((key) => [key, Number(row[key])])),
                    })),
                })}>Save</Button>
            </>,
        },
        {
            title: "Ripening programs",
            status: gatewayId && ripewise.enabled && !programsQuery.isLoading && !fruitsWithoutProgram.length ? "done" : "todo",
            body: <>
                <p className="text-muted">A program is the recipe a room runs: phases with temperature, ethylene and CO₂ settings. Create at least one program for each fruit you ripen.</p>
                {!gatewayId || !ripewise.enabled
                    ? <Alert color="secondary">Enable RipeWise and add a gateway first.</Alert>
                    : <>
                        <p><b>{programs.length}</b> programs on this gateway.</p>
                        {!!fruitsWithoutProgram.length && <p>No program yet for: {fruitsWithoutProgram.map((fruit) => fruit.text || fruit.value).join(", ")}.</p>}
                        <Button color="primary" tag={Link} to={`/device-management/${gatewayId}/view/ripening-programs`}>Manage programs</Button>
                    </>}
            </>,
        },
        {
            title: "Sensor data in the graph",
            status: stored.sensor_data ? "done" : "todo",
            body: <>
                <p className="text-muted">Where the room graph gets its sensor data: from EYE&apos;s own registers, from RipeWise, or one with the other as fallback when it has no data. Which sensors the graph shows is set in the <Link to={`/tenants/${tenantId}/tab/root`}>root config</Link> (sensor graph registers).</p>
                {[
                    ["eye", "EYE only"],
                    ["ripewise", "RipeWise only"],
                    ["eye_first", "EYE, RipeWise when EYE has none"],
                    ["ripewise_first", "RipeWise, EYE when RipeWise has none"],
                ].map(([value, label]) => (
                    <FormGroup check key={value}>
                        <Input id={`sensor-data-${value}`} type="radio" name="sensor_data" checked={ripewise.sensor_data === value} disabled={cannotSave} onChange={() => save({ sensor_data: value as RipewiseConfig["sensor_data"] })} />
                        <Label for={`sensor-data-${value}`} check>{label}</Label>
                    </FormGroup>
                ))}
            </>,
        },
        {
            title: "Control",
            status: "control" in stored ? "done" : "todo",
            body: <>
                <p className="text-muted">With control on, users start, stop and change programs from EYE. With control off, EYE only shows the room status and sensor data; the rooms are run from the controller itself.</p>
                <FormGroup check>
                    <Input id="control-on" type="radio" name="control" checked={"control" in stored && ripewise.control} disabled={cannotSave} onChange={() => save({ control: true })} />
                    <Label for="control-on" check>EYE controls the rooms</Label>
                </FormGroup>
                <FormGroup check>
                    <Input id="control-off" type="radio" name="control" checked={"control" in stored && !ripewise.control} disabled={cannotSave} onChange={() => save({ control: false })} />
                    <Label for="control-off" check>Read only</Label>
                </FormGroup>
            </>,
        },
        {
            title: "Check a room",
            status: linkedCells.length && ripewise.enabled ? "done" : "todo",
            body: <>
                <p className="text-muted">Open a linked room and check that it shows the live status of the controller and the sensor graph.</p>
                {linkedCells.length
                    ? <ul>{linkedCells.slice(0, 10).map((cell) => <li key={cell.location_id}><Link to={`/ripening-room/${cell.location_id}`}>{cell.label}</Link></li>)}</ul>
                    : <Alert color="secondary">Link a room first (step 3).</Alert>}
            </>,
        },
    ];

    const [current, setCurrent] = useState(0);
    const doneCount = steps.filter((step) => step.status === "done").length;
    const step = steps[current];
    const isLast = current === steps.length - 1;
    const openSteps = steps.map((item, index) => ({ ...item, index })).filter((item) => item.status !== "done");

    // * Typed but not saved yet; leaving asks first.
    const filledLimits = (rows: Record<string, Record<string, string>>) => JSON.stringify(Object.entries(rows)
        .map(([fruit, row]) => [fruit, FRUIT_LIMIT_KEYS.map((key) => row?.[key] ?? "")])
        .filter(([, values]) => (values as string[]).some((value) => value !== ""))
        .sort());
    const unsaved = [
        serviceTenantId.trim() !== (ripewise.tenant_id ?? "") && "the RipeWise tenant ID (step 1)",
        filledLimits(limits) !== filledLimits(storedLimits()) && "the temperature limits (step 4)",
    ].filter(Boolean) as string[];
    const navigate = useNavigate();
    const [confirmExit, setConfirmExit] = useState(false);
    const exit = (force = false) => {
        if (unsaved.length && !force) {
            setConfirmExit(true);
            return;
        }
        navigate(`/tenants/${tenantId}`);
    };

    return (
        <FullPageLayout>
            <FormContainer
                title="Set up RipeWise"
                subtitle={<span>Step by step, everything RipeWise needs for this tenant. Each step saves on its own, so you can stop and come back any time. Later changes go in the <Link to={`/tenants/${tenantId}/tab/${CONFIG_TYPES.RIPEWISE}`}>RipeWise config</Link>.</span>}
                actionsTop={<Button color="secondary" outline onClick={() => exit()}>Close setup</Button>}
            >
                {confirmExit && <Alert color="warning" className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <span>You have not saved {unsaved.join(" and ")}.</span>
                    <span className="d-flex gap-2">
                        <Button color="secondary" outline onClick={() => setConfirmExit(false)}>Stay</Button>
                        <Button color="warning" onClick={() => exit(true)}>Leave without saving</Button>
                    </span>
                </Alert>}
                <Progress className="mb-3" value={(doneCount / steps.length) * 100}>{doneCount} / {steps.length}</Progress>
                <Row>
                    <Col md={4} lg={3}>
                        <ol css={stepperCss} className="mb-4">
                            {steps.map((item, index) => (
                                <li key={item.title} className={`${item.status}${index === current ? " current" : ""}`}>
                                    <button type="button" aria-current={index === current ? "step" : undefined} onClick={() => setCurrent(index)}>
                                        <span className="circle">
                                            {item.status === "done" && <FontAwesomeIcon icon={faCheck} />}
                                            {item.status === "problem" && <FontAwesomeIcon icon={faExclamation} />}
                                            {item.status === "todo" && index + 1}
                                        </span>
                                        <span>
                                            <span className="title">{item.title}</span>
                                            <span className="status">{index === current ? "Current step" : STATUS_LABEL[item.status]}</span>
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ol>
                    </Col>
                    <Col md={8} lg={9}>
                        <div className="small text-muted text-uppercase fw-semibold">Step {current + 1} of {steps.length}</div>
                        <h4 className="d-flex align-items-center gap-2">
                            {step.title}
                            <Badge color={{ done: "success", problem: "warning", todo: "secondary" }[step.status]} pill className="fs-6 fw-normal">{STATUS_LABEL[step.status]}</Badge>
                        </h4>
                        {error && <Alert color="danger">{error}</Alert>}
                        {step.body}
                        {isLast && <Alert color={openSteps.length ? "info" : "success"} className="mt-4">
                            {openSteps.length
                                ? <>
                                    <b>Not finished yet.</b> Still to do:
                                    <ul className="mb-0">
                                        {openSteps.map((item) => (
                                            <li key={item.title}><Button color="link" className="p-0 align-baseline" onClick={() => setCurrent(item.index)}>{item.index + 1}. {item.title}</Button></li>
                                        ))}
                                    </ul>
                                </>
                                : <><b>RipeWise is set up.</b> Every step is done.</>}
                        </Alert>}
                        <div className="d-flex justify-content-between mt-4">
                            <Button color="secondary" outline disabled={current === 0} onClick={() => setCurrent(current - 1)}>Back</Button>
                            {isLast
                                ? <Button color="primary" onClick={() => exit()}>Finish setup</Button>
                                : <Button color="primary" onClick={() => setCurrent(current + 1)}>Next</Button>}
                        </div>
                    </Col>
                </Row>
            </FormContainer>
            <RoomLinksModal tenantId={tenantId} isOpen={roomLinksOpen} toggle={() => { setRoomLinksOpen(false); refetchCells(); }} />
        </FullPageLayout>
    );
}
